/**
 * useCryptoDeposit Composable
 *
 * Main logic for crypto deposit flow (Direct + Exchange unified)
 */

import { ref, computed } from 'vue';
import { useChangelly, type ChangellyCurrency, type ExchangeAmountResult, type FixRateForAmountResult } from './useChangelly';
import { usePopularNetworks } from './usePopularNetworks';
import { getCurrencyById, getNetworkConfig } from '../config/currencies';
import type { CurrencyConfig } from './useDepositMonitor';

export type DepositType = 'direct' | 'exchange';
export type DepositFlowState = 'selecting' | 'processing' | 'monitoring' | 'completed' | 'error';

export interface DepositOption {
  // Currency info from Changelly
  currency: ChangellyCurrency;

  // Network info (optional, some currencies have multiple networks)
  network?: {
    id: string;
    name: string;
    blockchain: string;
  };

  // Deposit type
  type: DepositType;

  // Exchange rate info (for exchange type)
  rate?: ExchangeAmountResult;
  rateLoading?: boolean;
  rateError?: string;

  // Fixed rate info (for exchange type)
  fixedRate?: FixRateForAmountResult;
  rateId?: string;          // Rate ID from getFixRateForAmount
  rateExpiredAt?: number;   // Unix timestamp when rate expires

  // Minimum amount
  minAmount?: string;

  // Display info
  displayName: string;
  displayNetwork?: string;
  isPopular?: boolean;
}

export interface DepositTransaction {
  type: DepositType;

  // For direct deposits
  address?: string;

  // For exchange deposits
  changellyTxId?: string;
  payinAddress?: string;
  amountExpectedFrom?: string;
  amountExpectedTo?: string;

  // Common
  fromCurrency: string;
  toCurrency: string;
  network?: string;
}

export interface UseCryptoDepositOptions {
  // Wallet info
  walletAddress: string;
  walletCurrency: CurrencyConfig;
  walletNetwork: { id: string; name: string };

  // Options
  loadRatesOnInit?: boolean;
}

/**
 * Convert wallet currency code + network to Changelly ticker
 * Changelly uses network-specific tickers for some currencies
 */
function getChangellyTicker(currencyCode: string, networkId: string): string {
  const code = currencyCode.toLowerCase();
  const network = networkId.toLowerCase();

  // USDC mappings
  if (code === 'usdc') {
    if (network.includes('base')) return 'usdcbase';
    if (network.includes('polygon') || network.includes('matic')) return 'usdcmatic';
    if (network.includes('arbitrum') || network.includes('arb')) return 'usdcarb';
    if (network.includes('optimism') || network.includes('opt')) return 'usdcop';
    if (network.includes('avalanche') || network.includes('avax')) return 'usdcavax';
    return 'usdc'; // Ethereum mainnet
  }

  // ETH mappings
  if (code === 'eth') {
    if (network.includes('base')) return 'ethbase';
    if (network.includes('arbitrum') || network.includes('arb')) return 'etharb';
    if (network.includes('optimism') || network.includes('opt')) return 'ethop';
    return 'eth'; // Ethereum mainnet
  }

  // For other currencies, return as-is
  return code;
}

export function useCryptoDeposit(options: UseCryptoDepositOptions) {
  const {
    walletAddress,
    walletCurrency,
    walletNetwork,
    loadRatesOnInit = false,
  } = options;

  // Composables
  const changelly = useChangelly();
  const popularNetworks = usePopularNetworks();

  // Get Changelly-compatible ticker for target currency
  const targetChangellyTicker = getChangellyTicker(walletCurrency.code, walletNetwork.id);
  console.log('[useCryptoDeposit] Using Changelly ticker:', targetChangellyTicker, 'for', walletCurrency.code, 'on', walletNetwork.id);

  // State
  const state = ref<DepositFlowState>('selecting');
  const depositOptions = ref<DepositOption[]>([]);
  const selectedOption = ref<DepositOption | null>(null);
  const transaction = ref<DepositTransaction | null>(null);
  const error = ref<Error | null>(null);
  const loading = ref(false);

  /**
   * Check if a currency/network combination is direct deposit
   */
  function isDirect(currency: ChangellyCurrency, network?: string): boolean {
    // Match currency ticker
    const currencyMatches =
      currency.ticker.toLowerCase() === walletCurrency.code.toLowerCase();

    // If no network specified for currency, just check currency
    if (!network) {
      return currencyMatches;
    }

    // Match network
    const networkMatches =
      network.toLowerCase() === walletNetwork.id.toLowerCase() ||
      network.toLowerCase() === walletCurrency.id.toLowerCase();

    return currencyMatches && networkMatches;
  }

  /**
   * Convert USD amount to target currency amount
   * Uses fixed conversion rates for stablecoins and approximate rates for crypto
   */
  function convertUsdToTargetCurrency(usd: number, targetCurrency: string): string {
    const currency = targetCurrency.toLowerCase();

    // Stablecoin: 1:1 with USD
    if (currency === 'usdc' || currency === 'usdt') {
      return usd.toString();
    }

    // Wrapped BTC: approximate rate $60,000 per BTC
    if (currency === 'cbbtc' || currency === 'btc' || currency === 'wbtc') {
      const BTC_USD_RATE = 60000;
      const btcAmount = usd / BTC_USD_RATE;
      return btcAmount.toFixed(8);
    }

    // ETH: approximate rate $3,000 per ETH
    if (currency === 'eth') {
      const ETH_USD_RATE = 3000;
      const ethAmount = usd / ETH_USD_RATE;
      return ethAmount.toFixed(8);
    }

    // Default: treat as 1:1 (fallback)
    console.warn(`[useCryptoDeposit] Unknown target currency: ${targetCurrency}, using 1:1 rate`);
    return usd.toString();
  }

  /**
   * Load all available deposit options
   */
  async function loadDepositOptions(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      // 1. Get currencies from Changelly
      const currenciesResult = await changelly.getCurrencies();
      if (!currenciesResult.success || !currenciesResult.currencies) {
        throw new Error('Failed to load currencies');
      }

      const currencies = currenciesResult.currencies.filter(
        (c) => c.enabled && c.enabledFrom
      );

      // 2. Get popular networks
      await popularNetworks.getPopularNetworks({ limit: 5 });
      const popularList = popularNetworks.popularNetworks.value;

      // 3. Build deposit options
      const options: DepositOption[] = [];

      for (const currency of currencies) {
        // Check if this is a direct deposit option
        const isDirectDeposit = isDirect(currency, currency.blockchain);

        // Check if popular
        const isPopular = popularList.some(
          (p) =>
            p.symbol.toLowerCase() === currency.ticker.toLowerCase() ||
            p.coingeckoId.toLowerCase() === currency.ticker.toLowerCase()
        );

        const option: DepositOption = {
          currency,
          network: currency.blockchain ? {
            id: currency.blockchain,
            name: currency.blockchain,
            blockchain: currency.blockchain,
          } : undefined,
          type: isDirectDeposit ? 'direct' : 'exchange',
          displayName: currency.fullName || currency.ticker.toUpperCase(),
          displayNetwork: currency.blockchain || undefined,
          isPopular,
          rateLoading: false,
        };

        options.push(option);
      }

      // Sort: popular first, then by type (direct before exchange), then alphabetically
      options.sort((a, b) => {
        if (a.isPopular !== b.isPopular) {
          return a.isPopular ? -1 : 1;
        }
        if (a.type !== b.type) {
          return a.type === 'direct' ? -1 : 1;
        }
        return a.displayName.localeCompare(b.displayName);
      });

      depositOptions.value = options;

      // 4. Load exchange rates for popular currencies automatically
      // This respects rate limits by only loading popular ones first
      if (loadRatesOnInit) {
        const popularExchangeOptions = options.filter(
          (opt) => opt.isPopular && opt.type === 'exchange'
        );
        if (popularExchangeOptions.length > 0) {
          // Load rates for popular currencies with delays
          await loadRatesForOptions(popularExchangeOptions);
        }
      }
    } catch (err) {
      console.error('[useCryptoDeposit] Failed to load options:', err);
      error.value = err instanceof Error ? err : new Error('Unknown error');
    } finally {
      loading.value = false;
    }
  }

  /**
   * Load exchange rates for all options (batch)
   * WARNING: This loads ALL rates at once and can hit rate limits
   * Use loadRatesForOptions() instead for better control
   */
  async function loadExchangeRates(): Promise<void> {
    const exchangeOptions = depositOptions.value.filter((opt) => opt.type === 'exchange');

    if (exchangeOptions.length === 0) {
      return;
    }

    // Mark all as loading
    exchangeOptions.forEach((opt) => {
      opt.rateLoading = true;
    });

    try {
      // Build pairs for batch query
      const pairs = exchangeOptions.map((opt) => ({
        from: opt.currency.ticker,
        to: targetChangellyTicker,
        amountFrom: '1',
      }));

      // Get rates
      const rateMap = await changelly.getBatchExchangeRates(pairs);

      // Update options with rates
      exchangeOptions.forEach((opt) => {
        const key = `${opt.currency.ticker}_${targetChangellyTicker}`;
        const result = rateMap.get(key);

        opt.rateLoading = false;

        if (result?.success && result.rate) {
          opt.rate = result.rate;
          opt.rateError = undefined;
        } else {
          opt.rateError = result?.error || 'Failed to load rate';
        }
      });
    } catch (err) {
      console.error('[useCryptoDeposit] Failed to load rates:', err);

      // Mark all as error
      exchangeOptions.forEach((opt) => {
        opt.rateLoading = false;
        opt.rateError = 'Failed to load rate';
      });
    }
  }

  /**
   * Load rate for a single option
   */
  async function loadRateForOption(option: DepositOption): Promise<void> {
    if (option.type === 'direct') {
      return; // Direct deposits don't need rates
    }

    if (option.rate || option.rateLoading) {
      return; // Already loaded or loading
    }

    option.rateLoading = true;

    try {
      const rate = await changelly.getExchangeAmount({
        from: option.currency.ticker,
        to: targetChangellyTicker,
        amountFrom: '1',
      });

      option.rate = rate;
      option.rateError = undefined;
    } catch (err) {
      console.error(`[useCryptoDeposit] Failed to load rate for ${option.currency.ticker}:`, err);
      option.rateError = err instanceof Error ? err.message : 'Failed to load rate';
    } finally {
      option.rateLoading = false;
    }
  }

  /**
   * Load rates for specific options with delays to respect rate limits
   * Changelly limit: 10 rps, so we process sequentially with 150ms delay between each
   */
  async function loadRatesForOptions(options: DepositOption[]): Promise<void> {
    const exchangeOptions = options.filter((opt) => opt.type === 'exchange');

    if (exchangeOptions.length === 0) {
      return;
    }

    const DELAY_MS = 150; // 150ms delay between requests (6.6 rps, safely under 10 rps limit)

    // Process sequentially with delays
    for (let i = 0; i < exchangeOptions.length; i++) {
      const option = exchangeOptions[i];

      await loadRateForOption(option);

      // Delay before next request (unless this is the last one)
      if (i < exchangeOptions.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, DELAY_MS));
      }
    }
  }

  /**
   * Calculate exchange estimate based on USD amount using Fixed Rate API
   * Returns estimated source and target amounts, stores rateId for transaction
   */
  async function calculateExchangeEstimate(usdAmount: number): Promise<{
    sourceAmount: string;
    targetAmount: string;
    sourceCurrency: string;
    targetCurrency: string;
  }> {
    if (!selectedOption.value || selectedOption.value.type !== 'exchange') {
      throw new Error('No exchange option selected');
    }

    try {
      loading.value = true;

      // 1. Convert USD to target currency
      const targetAmount = convertUsdToTargetCurrency(usdAmount, walletCurrency.code);

      // 2. Get fixed rate from Changelly using amountTo
      const result = await changelly.getFixRateForAmount({
        from: selectedOption.value.currency.ticker,
        to: targetChangellyTicker,
        amountTo: targetAmount,
      });

      // 3. Store fixed rate info in selectedOption (valid for 60 seconds)
      selectedOption.value.fixedRate = result;
      selectedOption.value.rateId = result.id;
      selectedOption.value.rateExpiredAt = result.expiredAt;

      console.log('[useCryptoDeposit] Fixed rate obtained:', {
        rateId: result.id,
        amountFrom: result.amountFrom,
        amountTo: result.amountTo,
        expiresAt: new Date(result.expiredAt * 1000).toISOString(),
      });

      return {
        sourceAmount: result.amountFrom || '0',
        targetAmount: result.amountTo || targetAmount,
        sourceCurrency: selectedOption.value.currency.ticker,
        targetCurrency: walletCurrency.code,
      };
    } catch (err) {
      console.error('[useCryptoDeposit] Failed to calculate estimate:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Select a deposit option
   */
  async function selectOption(option: DepositOption): Promise<void> {
    selectedOption.value = option;

    // If direct, no need to create transaction - just show QR
    if (option.type === 'direct') {
      transaction.value = {
        type: 'direct',
        address: walletAddress,
        fromCurrency: option.currency.ticker,
        toCurrency: walletCurrency.code,
        network: option.network?.id,
      };
      state.value = 'monitoring';
      return;
    }

    // If exchange, don't load minAmount here - save API calls
    // UI will call calculateExchangeEstimate with user's USD amount
    // minAmount can be loaded on-demand if needed

    // Stay in 'selecting' state - UI will show amount input
    // state.value remains 'selecting' until transaction is created
  }

  /**
   * Create exchange transaction using Fixed Rate API
   */
  async function createExchangeTransaction(amountFrom: string): Promise<void> {
    if (!selectedOption.value || selectedOption.value.type !== 'exchange') {
      throw new Error('No exchange option selected');
    }

    // Check if we have a valid rateId
    if (!selectedOption.value.rateId || !selectedOption.value.rateExpiredAt) {
      throw new Error('No fixed rate available. Please recalculate estimate.');
    }

    // Check if rateId has expired (60 seconds validity)
    const now = Math.floor(Date.now() / 1000);
    if (now >= selectedOption.value.rateExpiredAt) {
      throw new Error('Fixed rate has expired. Please recalculate estimate.');
    }

    try {
      loading.value = true;
      state.value = 'processing';

      console.log('[useCryptoDeposit] Creating fixed rate transaction:', {
        rateId: selectedOption.value.rateId,
        amountFrom,
        timeRemaining: selectedOption.value.rateExpiredAt - now,
      });

      const result = await changelly.createFixTransaction({
        from: selectedOption.value.currency.ticker,
        to: targetChangellyTicker,
        address: walletAddress,
        rateId: selectedOption.value.rateId,
        amountFrom,
        refundAddress: walletAddress, // Use same address for refunds
      });

      transaction.value = {
        type: 'exchange',
        changellyTxId: result.id,
        payinAddress: result.payinAddress,
        amountExpectedFrom: result.amountExpectedFrom,
        amountExpectedTo: result.amountExpectedTo,
        fromCurrency: selectedOption.value.currency.ticker,
        toCurrency: walletCurrency.code,
        network: selectedOption.value.network?.id,
      };

      console.log('[useCryptoDeposit] Fixed rate transaction created:', result.id);
      state.value = 'monitoring';
    } catch (err) {
      console.error('[useCryptoDeposit] Failed to create fixed rate transaction:', err);
      error.value = err instanceof Error ? err : new Error('Failed to create transaction');
      state.value = 'error';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Reset flow
   */
  function reset(): void {
    state.value = 'selecting';
    selectedOption.value = null;
    transaction.value = null;
    error.value = null;
  }

  /**
   * Go back to selection
   */
  function backToSelection(): void {
    selectedOption.value = null;
    transaction.value = null;
    state.value = 'selecting';
  }

  // Computed
  const popularOptions = computed(() =>
    depositOptions.value.filter((opt) => opt.isPopular)
  );

  const directOptions = computed(() =>
    depositOptions.value.filter((opt) => opt.type === 'direct')
  );

  const exchangeOptions = computed(() =>
    depositOptions.value.filter((opt) => opt.type === 'exchange')
  );

  const hasSelectedOption = computed(() => selectedOption.value !== null);
  const hasTransaction = computed(() => transaction.value !== null);

  return {
    // State
    state: computed(() => state.value),
    depositOptions: computed(() => depositOptions.value),
    popularOptions,
    directOptions,
    exchangeOptions,
    selectedOption: computed(() => selectedOption.value),
    transaction: computed(() => transaction.value),
    error: computed(() => error.value),
    loading: computed(() => loading.value || changelly.loading.value),

    // Computed flags
    hasSelectedOption,
    hasTransaction,

    // Methods
    loadDepositOptions,
    loadExchangeRates,
    loadRatesForOptions,
    loadRateForOption,
    selectOption,
    calculateExchangeEstimate,
    createExchangeTransaction,
    reset,
    backToSelection,
    isDirect,
  };
}
